import { Fragment, useContext, useMemo } from "react";
import { useIntl } from "react-intl";
import { Link, useLocation } from "react-router";

import {
  BuilderContext,
  type RegisteredComponent,
} from "@builder.io/sdk-react";

import { useURL } from "~/contexts";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "~/hec/components/ui/breadcrumb";
import { useTopicTitle } from "~/hooks/learning-center/use-topic-title";

import messages from "./messages";
import {
  buildArticleBreadcrumbTrail,
  resolveArticleBreadcrumbState,
} from "./trail";

/**
 * SFCC-11240 (ARTICLES-13) — port of hec-composable's
 * `LearningCenterArticleBreadcrumb` Builder block. Renders the breadcrumb trail
 * on HSA Learning Center article pages, registered in `customComponents` and
 * placed via Builder on the live `learning-center-article-page` entries.
 *
 * Three URL-driven states (mirrors composable `learningCenterArticleBreadcrumb.jsx`):
 *  - `article`        — an article detail page (`/learning-center/articles/<slug>`):
 *                       trail ends in the article title (non-clickable).
 *  - `article-topic`  — the articles listing (`/learning-center/articles`, optionally
 *                       `?topic=`): trail ends in the topic title, or just `Articles`.
 *  - null             — not an LC article page → renders nothing.
 *
 * Article-title source: the current Builder entry's `data.title`, read from
 * `BuilderContext.content.data` (the same `@builder.io/sdk-react` v4 API the
 * composable uses — both repos are on 4.2.x). Labels/links are HSA-hardcoded,
 * matching the composable (Learning Center is HSA-only).
 *
 * Topic-title enrichment (SFCC-11240): in the `article-topic` state with a
 * `?topic=` filter, the topic's display title is resolved from the
 * `learning-center-article-topic` model (composable
 * `useFetchOneEntry(learning-center-article-topic)`). Workers has no in-browser
 * Builder fetch, so it goes through the resource-route + `useFetcher` convention
 * (`useTopicTitle` → `/resources/article-topic-title`). A resolved title renders
 * the trailing `… > Articles > <topic>` crumb; a missing/unresolvable topic
 * degrades to the plain `Articles` fallback (matching the composable's no-topic
 * branch). While the topic fetch is in flight the trail renders nothing, so the
 * `Articles` fallback never flashes before the topic crumb resolves.
 */

const LearningCenterArticleBreadcrumb = ({
  separator = ">",
  attributes,
}: {
  separator?: string;
  attributes?: Record<string, unknown> & { className?: string };
}) => {
  const location = useLocation();
  const url = useURL();
  const intl = useIntl();
  const builderContext = useContext(BuilderContext);
  // Harden against a non-string `title` (e.g. a rich-text object) so we never
  // pass a non-renderable value through to `{crumb.label}`.
  const rawTitle = (
    builderContext?.content?.data as { title?: unknown } | undefined
  )?.title;
  const articleTitle = typeof rawTitle === "string" ? rawTitle : undefined;

  const state = useMemo(
    () => resolveArticleBreadcrumbState(location.pathname),
    [location.pathname],
  );

  // Topic title enrichment (SFCC-11240): only the `article-topic` state honors a
  // `?topic=` filter. Pass the slug to the fetcher hook solely in that state so
  // other states (and the no-filter listing) never issue a request.
  const topicSlug =
    state === "article-topic"
      ? new URLSearchParams(location.search).get("topic")?.trim() || undefined
      : undefined;
  const { title: topicTitle, isLoading: topicTitleLoading } =
    useTopicTitle(topicSlug);

  // Mirror the composable: while the topic title is resolving, render nothing so
  // the `Articles` fallback never flashes before the `… > <topic>` crumb lands.
  if (topicTitleLoading) return null;

  const crumbs = buildArticleBreadcrumbTrail({
    state,
    articleTitle,
    topicTitle,
    labels: {
      home: intl.formatMessage(messages.home),
      learningCenter: intl.formatMessage(messages.learningCenter),
      articles: intl.formatMessage(messages.articles),
    },
  });

  if (crumbs.length === 0) return null;

  const { className, ...rest } = attributes ?? {};

  return (
    <Breadcrumb
      className={className}
      data-testid="learningCenterArticleBreadcrumb"
      {...rest}
    >
      <BreadcrumbList
        data-testid="lc-article-breadcrumb-list"
        className="font-grotesque text-sm font-light leading-5.5 text-blue-03"
      >
        {crumbs.map((crumb, i) => (
          <Fragment key={`${crumb.label}-${i}`}>
            <BreadcrumbItem data-testid="lc-article-breadcrumb-item">
              {crumb.href ? (
                <Link
                  to={url(crumb.href)}
                  prefetch="intent"
                  className="font-light text-blue-03 underline hover:no-underline"
                >
                  {crumb.label}
                </Link>
              ) : (
                <BreadcrumbPage
                  data-testid="lc-article-breadcrumb-current"
                  className="font-light text-blue-04"
                >
                  {crumb.label}
                </BreadcrumbPage>
              )}
            </BreadcrumbItem>
            {i < crumbs.length - 1 && (
              <BreadcrumbSeparator
                data-testid="lc-article-breadcrumb-separator"
                className="text-blue-03"
              >
                {separator}
              </BreadcrumbSeparator>
            )}
          </Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
};

export const LearningCenterArticleBreadcrumbDefinition = {
  component: LearningCenterArticleBreadcrumb,
  name: "LearningCenterArticleBreadcrumb",
  noWrap: true,
  canHaveChildren: false,
  inputs: [
    {
      name: "separator",
      type: "string",
      defaultValue: ">",
      friendlyName: "Breadcrumb Separator",
      helperText:
        "Character shown between crumbs. Only renders on the live article page — the trail is empty in the Builder editor canvas (no article context).",
    },
  ],
} satisfies RegisteredComponent;

// COM-445: additive, registry-only alias for HSA learning-center article
// entries authored under MRT, where this block was registered as "Breadcrumb"
// (hec-composable `standardBreadcrumb.jsx`). ~14 evergreen production entries
// still carry that name and logged 13–28k `Could not find a registered
// component named "Breadcrumb"` warnings/day with nothing rendered in its
// place. Spreads the definition above, so the alias shares its component,
// inputs, `noWrap` and `canHaveChildren` by construction — only the registered
// name differs. The SDK spreads the stored options onto a noWrap component as
// props, so the legacy `type` / `customCrumbs` / `includeHome` arrive and are
// ignored (the component destructures only `separator` + `attributes`). The
// stored `BreadcrumbLink` child is passed as `children` and dropped because
// this component never renders `children` — `canHaveChildren` is editor-only
// metadata and does not gate that. Do NOT start rendering `children` here
// without re-checking those legacy entries. Off article URLs the component
// renders null, so the alias is inert anywhere else a legacy "Breadcrumb"
// might appear. Hidden from the insert menu — new content must use the Workers
// name. Same alias pattern as DeprecatedCarouselContentDefinition (SFCC-11736)
// and ProductRecommendationsLegacyDefinition (SFCC-11794).
export const LegacyBreadcrumbDefinition = {
  ...LearningCenterArticleBreadcrumbDefinition,
  name: "Breadcrumb",
  hideFromInsertMenu: true,
} satisfies RegisteredComponent;

export default LearningCenterArticleBreadcrumb;
